/**
 * queryKey ファクトリ（05-ui §5.3）。
 *
 * 規約:
 *  1. 先頭は必ず `'ops'`。ログアウト時に `removeQueries({queryKey:['ops']})` で一掃する。
 *  2. 一覧クエリの末尾は **フィルタオブジェクトそのもの**。URL の search params と 1:1 なので、
 *     URL が変われば自動的に別キャッシュになる。
 *  3. mutation 成功時は「一番浅い共通キー」を invalidate する。
 */

export type QueryFilters = Record<string, unknown> | undefined;

export const qk = {
  root: ['ops'] as const,

  publicConfig: () => ['ops', 'public-config'] as const,
  me: () => ['ops', 'me'] as const,
  sessions: () => ['ops', 'sessions'] as const,

  dashboard: () => ['ops', 'dashboard'] as const,
  /**
   * 更新通知の能力（`GET /ops/events`）。
   * SSE 非対応の宣言とポーリング間隔しか返らないので、1 本のキーで足りる。
   */
  eventsCapability: () => ['ops', 'events', 'capability'] as const,
  /** 全組織横断の日次使用量（`GET /ops/usage`）。集計は画面側で行う。 */
  platformUsage: () => ['ops', 'usage', 'platform'] as const,

  nodes: (f?: QueryFilters) => (f === undefined ? (['ops', 'nodes'] as const) : (['ops', 'nodes', f] as const)),
  nodeGroups: () => ['ops', 'node-groups'] as const,
  node: (id: string) => ['ops', 'node', id] as const,
  nodeMetrics: (id: string, range: string) => ['ops', 'node', id, 'metrics', range] as const,
  nodeState: (id: string) => ['ops', 'node', id, 'state'] as const,
  nodeCommands: (id: string) => ['ops', 'node', id, 'commands'] as const,
  nodeDomains: (id: string, f?: QueryFilters) =>
    f === undefined ? (['ops', 'node', id, 'domains'] as const) : (['ops', 'node', id, 'domains', f] as const),

  orgs: (f?: QueryFilters) => (f === undefined ? (['ops', 'orgs'] as const) : (['ops', 'orgs', f] as const)),
  org: (id: string) => ['ops', 'org', id] as const,
  orgDomains: (id: string, f?: QueryFilters) =>
    f === undefined ? (['ops', 'org', id, 'domains'] as const) : (['ops', 'org', id, 'domains', f] as const),
  orgMailboxes: (id: string, f?: QueryFilters) =>
    f === undefined ? (['ops', 'org', id, 'mailboxes'] as const) : (['ops', 'org', id, 'mailboxes', f] as const),
  orgUsage: (id: string, range: string) => ['ops', 'org', id, 'usage', range] as const,
  orgInvoices: (id: string) => ['ops', 'org', id, 'invoices'] as const,
  orgAudit: (id: string) => ['ops', 'org', id, 'audit'] as const,

  domains: (f?: QueryFilters) => (f === undefined ? (['ops', 'domains'] as const) : (['ops', 'domains', f] as const)),
  domain: (id: string) => ['ops', 'domain', id] as const,
  domainDns: (id: string) => ['ops', 'domain', id, 'dns'] as const,
  domainMailboxes: (id: string, f?: QueryFilters) =>
    f === undefined ? (['ops', 'domain', id, 'mailboxes'] as const) : (['ops', 'domain', id, 'mailboxes', f] as const),

  mailboxes: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'mailboxes'] as const) : (['ops', 'mailboxes', f] as const),

  jobs: (f?: QueryFilters) => (f === undefined ? (['ops', 'jobs'] as const) : (['ops', 'jobs', f] as const)),
  job: (id: string) => ['ops', 'job', id] as const,
  queues: () => ['ops', 'queues'] as const,
  deadLetter: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'dead-letter'] as const) : (['ops', 'dead-letter', f] as const),
  outbox: (f?: QueryFilters) => (f === undefined ? (['ops', 'outbox'] as const) : (['ops', 'outbox', f] as const)),
  outboxSummary: () => ['ops', 'outbox', 'summary'] as const,
  workers: () => ['ops', 'workers'] as const,
  scheduledRuns: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'scheduled-runs'] as const) : (['ops', 'scheduled-runs', f] as const),
  jobSchedules: () => ['ops', 'job-schedules'] as const,

  /* R0 の運用画面（バックアップ / 到達性 / 一斉配信 / インシデント） */
  backupHealth: () => ['ops', 'backup', 'health'] as const,
  restoreRuns: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'backup', 'restore-runs'] as const) : (['ops', 'backup', 'restore-runs', f] as const),
  backupDestinations: () => ['ops', 'backup', 'destinations'] as const,
  drDrills: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'backup', 'dr-drills'] as const) : (['ops', 'backup', 'dr-drills', f] as const),

  rblListings: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'deliverability', 'rbl'] as const) : (['ops', 'deliverability', 'rbl', f] as const),
  dnsChecks: () => ['ops', 'deliverability', 'dns-checks'] as const,
  ipWarmup: () => ['ops', 'deliverability', 'ip-warmup'] as const,
  serviceDns: () => ['ops', 'deliverability', 'service-dns'] as const,

  slaCredits: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'sla-credits'] as const) : (['ops', 'sla-credits', f] as const),
  incidents: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'incidents'] as const) : (['ops', 'incidents', f] as const),
  incident: (id: string) => ['ops', 'incident', id] as const,

  opsCampaigns: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'campaigns'] as const) : (['ops', 'campaigns', f] as const),
  /** 個別キャンペーン（`campaign_batches` の状態別件数を含む）。 */
  opsCampaign: (id: string) => ['ops', 'campaigns', 'detail', id] as const,
  killSwitch: () => ['ops', 'bulk', 'kill-switch'] as const,

  dkimKeys: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'dkim-keys'] as const) : (['ops', 'dkim-keys', f] as const),
  webmailDrift: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'webmail', 'drift'] as const) : (['ops', 'webmail', 'drift', f] as const),
  webmailInstances: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'webmail', 'instances'] as const) : (['ops', 'webmail', 'instances', f] as const),
  nodeUpdates: (id: string) => ['ops', 'node', id, 'updates'] as const,

  updateCatalog: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'updates', 'catalog'] as const) : (['ops', 'updates', 'catalog', f] as const),
  rollouts: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'updates', 'rollouts'] as const) : (['ops', 'updates', 'rollouts', f] as const),
  rollout: (id: string) => ['ops', 'updates', 'rollout', id] as const,

  blocklist: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'security', 'blocklist'] as const) : (['ops', 'security', 'blocklist', f] as const),
  blocklistStats: () => ['ops', 'security', 'blocklist', 'stats'] as const,
  sendingViolations: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'security', 'sending'] as const) : (['ops', 'security', 'sending', f] as const),
  abuseReports: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'security', 'abuse'] as const) : (['ops', 'security', 'abuse', f] as const),
  impersonations: (f?: QueryFilters) =>
    f === undefined
      ? (['ops', 'security', 'impersonations'] as const)
      : (['ops', 'security', 'impersonations', f] as const),

  audit: (f?: QueryFilters) => (f === undefined ? (['ops', 'audit'] as const) : (['ops', 'audit', f] as const)),

  /* 問い合わせ・権利行使要求・運営通知（A97 / A147） */
  supportTickets: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'support', 'tickets'] as const) : (['ops', 'support', 'tickets', f] as const),
  supportTicket: (id: string) => ['ops', 'support', 'ticket', id] as const,
  supportSummary: () => ['ops', 'support', 'summary'] as const,
  privacyRequests: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'privacy', 'requests'] as const) : (['ops', 'privacy', 'requests', f] as const),
  notifications: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'notifications'] as const) : (['ops', 'notifications', f] as const),
  notificationUnread: () => ['ops', 'notifications', 'unread-count'] as const,

  /* Webメールの AI（プラットフォーム既定と組織ごとの上書き） */
  aiPlatform: () => ['ops', 'ai', 'platform'] as const,
  /** AI 接続先の在庫（`ai_provider_endpoints`）。ルーティングの候補そのもの。 */
  aiEndpoints: () => ['ops', 'ai', 'endpoints'] as const,
  /**
   * アグリゲータのプリセット表（0060）。コードに持っている定数を返す API なので
   * 実質不変。それでもキャッシュを分けておくのは、接続先ダイアログを開くたびに
   * 引き直さないため。
   */
  aiPresets: () => ['ops', 'ai', 'presets'] as const,
  aiOrgs: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'ai', 'orgs'] as const) : (['ops', 'ai', 'orgs', f] as const),
  aiOrg: (id: string) => ['ops', 'ai', 'org', id] as const,

  /* 告知・計画メンテナンス（R66 / A174） */
  announcements: () => ['ops', 'announcements'] as const,
  maintenanceWindows: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'maintenance-windows'] as const) : (['ops', 'maintenance-windows', f] as const),

  plans: () => ['ops', 'plans'] as const,
  subscriptions: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'subscriptions'] as const) : (['ops', 'subscriptions', f] as const),
  closePreview: (yyyyMM?: string) =>
    yyyyMM === undefined
      ? (['ops', 'billing', 'close-preview'] as const)
      : (['ops', 'billing', 'close-preview', yyyyMM] as const),
  invoices: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'invoices'] as const) : (['ops', 'invoices', f] as const),
  invoice: (id: string) => ['ops', 'invoice', id] as const,
  /** 入金の横断一覧（R86）と売掛のエイジング（A69）。 */
  payments: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'payments'] as const) : (['ops', 'payments', f] as const),
  billingAging: () => ['ops', 'billing', 'aging'] as const,
  /** 月次の売上・実収サマリ（A69）。期間はサーバーの既定（直近 12 か月）。 */
  billingSummary: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'billing', 'summary'] as const) : (['ops', 'billing', 'summary', f] as const),

  /* レピュテーション登録（R48）と共有配信テンプレート（A321） */
  reputationPrograms: () => ['ops', 'reputation-programs'] as const,
  contentTemplates: () => ['ops', 'content-templates'] as const,

  /**
   * break-glass（配信本文の閲覧統制・A321）。
   *
   * 申請と付与を**別のキーにする**。申請は Redis（24 時間で消える）、
   * 付与は DB で永続。同じキーにまとめると、承認したときにどちらを
   * invalidate すべきかが曖昧になる（承認は「申請が消えて付与が増える」）。
   */
  breakGlassRequests: () => ['ops', 'break-glass', 'requests'] as const,
  breakGlassGrants: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'break-glass', 'grants'] as const) : (['ops', 'break-glass', 'grants', f] as const),

  /**
   * プライバシー保護（画像プロキシの出口 IP / 既知トラッカー / 取得実績）。
   * 3 つは別の表なので別キーにする（1 つ足したときに全部を引き直さない）。
   */
  proxyEgressIps: () => ['ops', 'privacy', 'egress-ips'] as const,
  trackerHosts: () => ['ops', 'privacy', 'tracker-hosts'] as const,
  imageProxyHosts: (f?: QueryFilters) =>
    f === undefined
      ? (['ops', 'privacy', 'image-proxy-hosts'] as const)
      : (['ops', 'privacy', 'image-proxy-hosts', f] as const),

  /**
   * プライベートメールアドレスの濫用監視。
   * 集計・速度・明細で別キーにする（明細は組織を選んだときだけ引く）。
   */
  privateAddressStats: () => ['ops', 'private-addresses', 'stats'] as const,
  privateAddressVelocity: (f?: QueryFilters) =>
    f === undefined
      ? (['ops', 'private-addresses', 'velocity'] as const)
      : (['ops', 'private-addresses', 'velocity', f] as const),
  privateAddresses: (f?: QueryFilters) =>
    f === undefined
      ? (['ops', 'private-addresses', 'list'] as const)
      : (['ops', 'private-addresses', 'list', f] as const),

  staff: () => ['ops', 'staff'] as const,
  settings: () => ['ops', 'settings'] as const,
  certificates: (f?: QueryFilters) =>
    f === undefined ? (['ops', 'certificates'] as const) : (['ops', 'certificates', f] as const),
} as const;
